'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { SheetContent } from '@/components/ui/sheet';
import { useResponsiveSheetSide } from '@/presentation/hooks/useResponsiveSheetSide';
import { cn } from '@/libs/utils';
import { WalletSheetResizeControl } from './WalletSheetResizeControl';

interface WalletSheetLayoutProps {
  open: boolean;
  header: ReactNode;
  children: ReactNode;
}

export function WalletSheetLayout({ open, header, children }: WalletSheetLayoutProps) {
  const side = useResponsiveSheetSide();
  const mobile = side === 'bottom';
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!open || !mobile) setExpanded(false);
  }, [open, mobile]);

  return (
    <SheetContent
      side={side}
      data-testid="shopping-wallet-drawer"
      className={cn(
        'w-full overflow-y-auto bg-[#F4F4F4] p-0',
        '[&>button]:right-5 [&>button]:top-5 [&>button]:flex [&>button]:size-11 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-full [&>button]:bg-[#F3F4F6] [&>button]:opacity-100 [&>button]:ring-motitoBlue',
        '[&>button:focus:not(:focus-visible)]:ring-0 [&>button:focus:not(:focus-visible)]:ring-offset-0 [&>button>svg]:size-[18px] [&>button>svg]:text-[#0C0C0C]',
        !mobile && 'border-l-0 sm:max-w-md',
        mobile && 'overflow-hidden rounded-t-2xl border-l-0 transition-[height] motion-reduce:transition-none motion-reduce:animate-none [&>button]:top-[44px]',
        mobile && (expanded ? 'h-[90vh] supports-[height:100dvh]:h-[90dvh]' : 'h-[70vh] supports-[height:100dvh]:h-[70dvh]'),
      )}
    >
      <div className={cn('relative min-h-full', mobile && 'h-full min-h-0')}>
        <div className="absolute inset-x-0 top-0 h-32 bg-[linear-gradient(180deg,#FEF3C7_0%,#FFFBEB_55%,#F4F4F4_100%)]" />
        <div className={cn('relative flex min-h-full flex-col', mobile && 'h-full min-h-0')}>
          <div className="shrink-0">
            {mobile ? <WalletSheetResizeControl expanded={expanded} onExpandedChange={setExpanded} /> : null}
            {header}
          </div>
          <div
            data-testid="wallet-sheet-body"
            tabIndex={mobile ? 0 : undefined}
            role={mobile ? 'region' : undefined}
            aria-label={mobile ? 'Wallet contents' : undefined}
            className={cn('space-y-5 px-5 pb-6', mobile && 'min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[calc(1.5rem+env(safe-area-inset-bottom))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-motitoBlue')}
          >
            {children}
          </div>
        </div>
      </div>
    </SheetContent>
  );
}
